import React from 'react';
import { useSafeLazyQuery } from 'hooks/useSafeLazyQuery';
import { OrderManagementSuggestionFilterName } from 'pages/OrderManagement/components/OMComboSearch/constants';
import { AutoCompleteOptionTuple, DEFAULT_AUTOCOMPLETE_OPTIONS } from 'types';
import {
  SEARCH_ORDERS_BY_ORDER_NUMBER,
  GET_COLLABORATORS_FULL_TEXT_SEARCH,
  GET_COLLABORATOR,
} from './queries';
import {
  updateCollaboratorOptionsMap,
  getExtendedCollaboratorOptions,
} from '../utils';
import { CollaboratorOptionsMap } from '../types';
import { selectOMSearchByKey } from 'store/slices/order_management/search/selectors';
import { OMSearchFilterKeys } from 'store/slices/order_management/search/constants';
import { useSelector } from 'react-redux';

export const useGetOrderLookAhead = (
  setSelectedSuggestion: React.Dispatch<
    React.SetStateAction<AutoCompleteOptionTuple | null>
  >,
  autocompleteOptions: AutoCompleteOptionTuple[]
): void => {
  const lookAheadOptionKey = useSelector(
    selectOMSearchByKey(OMSearchFilterKeys.LOOKAHEAD_OPTION_TYPE)
  );
  const lookAheadOption = useSelector(
    selectOMSearchByKey(OMSearchFilterKeys.LOOKAHEAD_OPTION)
  );

  const [fetchCollaborator] = useSafeLazyQuery(GET_COLLABORATOR, {
    onCompleted(data) {
      const { collaborator } = data;
      const collaboratorSuggestion: AutoCompleteOptionTuple = [
        'recipientCollaboratorId',
        collaborator?.id,
        `${collaborator?.firstName?.trim()} ${collaborator?.lastName?.trim()} (${
          collaborator?.email ?? ''
        })`,
      ];
      setSelectedSuggestion(collaboratorSuggestion);
    },
  });

  React.useEffect(() => {
    if (!lookAheadOptionKey || !lookAheadOption) {
      setSelectedSuggestion(null);
      return;
    }

    if (lookAheadOptionKey === 'recipient') {
      const collaboratorOption = autocompleteOptions.find(
        (option: AutoCompleteOptionTuple) => option[1] === lookAheadOption
      );
      if (collaboratorOption) {
        setSelectedSuggestion(collaboratorOption);
      } else {
        fetchCollaborator({ variables: { id: lookAheadOption } });
      }
    } else if (lookAheadOptionKey === 'orderNumber') {
      setSelectedSuggestion([
        lookAheadOptionKey,
        lookAheadOption,
        `Order number: ${lookAheadOption}`,
      ]);
    }
  }, [lookAheadOptionKey, lookAheadOption]);
};

export const useGetOrderSuggestions = (
  searchTerm: string,
  filterName: OrderManagementSuggestionFilterName,
  setAutocompleteOptions: React.Dispatch<
    React.SetStateAction<AutoCompleteOptionTuple[]>
  >
) => {
  const lookAheadOption = useSelector(
    selectOMSearchByKey(OMSearchFilterKeys.LOOKAHEAD_OPTION)
  );

  const [fetchOrderOptionsByOrderNumber] = useSafeLazyQuery(
    SEARCH_ORDERS_BY_ORDER_NUMBER,
    {
      onCompleted(data) {
        const { searchOrders } = data;
        const { orders } = searchOrders;
        if (!orders?.length) return;
        const orderOptions = orders
          .slice()
          .sort((a: any, b: any) => a.orderNumber.localeCompare(b.orderNumber))
          .filter(
            (order: any, index: number) =>
              !(index > 0 && order.id === orders[index - 1].id)
          )
          .map((order: any) => [
            'orderNumber',
            order?.orderNumber,
            `Order number: ${order?.orderNumber}`,
          ])
          .filter((option: AutoCompleteOptionTuple) =>
            lookAheadOption ? option[1] === lookAheadOption : true
          );
        setAutocompleteOptions(orderOptions);
      },
      fetchPolicy: 'network-only',
    }
  );

  const [fetchOrderOptionsByCollaboratorFullTextSearch] = useSafeLazyQuery(
    GET_COLLABORATORS_FULL_TEXT_SEARCH,
    {
      fetchPolicy: 'network-only',
      onCompleted(data) {
        const collaboratorOptionsMap: CollaboratorOptionsMap = {};
        const { collaborators: fetchedCollaborators } = data;
        const { collaborators: collaboratorsData } = fetchedCollaborators;
        if (!collaboratorsData.length) return;
        const collaboratorOptions = collaboratorsData
          .slice()
          .sort((a: any, b: any) => a.lastName.localeCompare(b.lastName))
          .map((collaborator: any) => {
            updateCollaboratorOptionsMap(
              collaboratorOptionsMap,
              collaborator,
              'personalEmail'
            );
            updateCollaboratorOptionsMap(
              collaboratorOptionsMap,
              collaborator,
              'workEmail'
            );
            return [
              'recipientCollaboratorId',
              collaborator?.id,
              `${collaborator?.firstName?.trim()} ${collaborator?.lastName?.trim()} (${
                collaborator.email ?? ''
              })`,
            ];
          });
        const extendedCollaboratorOptions = getExtendedCollaboratorOptions(
          collaboratorOptionsMap,
          collaboratorOptions
        ).filter((option: AutoCompleteOptionTuple) =>
          lookAheadOption ? option[1] === lookAheadOption : true
        );
        setAutocompleteOptions(extendedCollaboratorOptions);
      },
    }
  );

  React.useEffect(() => {
    if (searchTerm) {
      switch (filterName) {
        case OrderManagementSuggestionFilterName.ORDER_NUMBER:
          fetchOrderOptionsByOrderNumber({
            variables: {
              orderNumber: searchTerm,
            },
          });
          break;
        case OrderManagementSuggestionFilterName.RECIPIENT:
          fetchOrderOptionsByCollaboratorFullTextSearch({
            variables: {
              collaboratorFullTextSearch: searchTerm,
            },
          });
          break;
        default:
          break;
      }
    } else if (!lookAheadOption) {
      setAutocompleteOptions(DEFAULT_AUTOCOMPLETE_OPTIONS);
    }
  }, [filterName, searchTerm, lookAheadOption]);
};
